/**
 * CreateInviteModal - Staff interface for creating contractor/visitor invites
 * Integrates with Phase 3 RPC functions and email automation
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { supabase } from '@shared/supabaseClient';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { Switch } from '@shared/components/ui/switch';
import { Mail, Send, AlertCircle, CheckCircle, ClipboardList, History, RotateCcw, Users, User, FolderKanban, Building2, Calendar, HardHat, Briefcase, Eye } from 'lucide-react';
import { createInvite } from '@shared/services/staffInviteService';

const CreateInviteModal = ({ isOpen, onClose, onSuccess, depots = [], defaultDepotId = null, currentUser = null }) => {
  // Auto-populate requesting person with logged-in user
  const defaultRequestingPerson = currentUser 
    ? `${currentUser.name || currentUser.email} <${currentUser.email}>`
    : '';

  // ── Mode: 'individual' | 'team' ──────────────────────────────────────────
  const [inviteMode, setInviteMode] = useState('individual');

  const [formData, setFormData] = useState({
    // Person / Project Lead Details
    person_name: '',
    person_email: '',
    person_company: '',
    person_phone: '',
    person_type: 'contractor',
    // Visit / Site Details
    depot_id: defaultDepotId,
    visit_date: new Date().toISOString().split('T')[0],
    nature_of_work: '',
    work_area: '',
    reason_for_visit: '',
    requesting_person: defaultRequestingPerson,
    expiry_hours: 72,
    send_email: true,
    brief_ids: [],
    // Project-specific fields (team mode)
    project_name: '',
    project_summary: '',
    estimated_start_date: '',
    estimated_end_date: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [availableBriefs, setAvailableBriefs] = useState([]);
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [inductionHistory, setInductionHistory] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [forceFullInduction, setForceFullInduction] = useState({});
  const [appUsers, setAppUsers] = useState([]);
  const [appUsersLoading, setAppUsersLoading] = useState(false);
  // 'select' = pick from list, 'manual' = free-text
  const [responsiblePersonMode, setResponsiblePersonMode] = useState('select');

  // Auto-populate depot from admin user's assigned depot when modal opens.
  // depots is in the dep array so we also re-sync once the list arrives.
  useEffect(() => {
    if (isOpen && defaultDepotId && depots.length > 0) {
      setFormData(prev => ({ ...prev, depot_id: prev.depot_id || defaultDepotId }));
    }
  }, [isOpen, defaultDepotId, depots]);

  // Load app users for responsible person dropdown
  useEffect(() => {
    async function loadUsers() {
      setAppUsersLoading(true);
      try {
        let query = supabase
          .from('users')
          .select('id, first_name, surname, email, depot_id')
          .eq('is_active', true)
          .order('first_name');
        // Scope to selected depot or show all if none chosen
        if (formData.depot_id) {
          query = query.or(`depot_id.eq.${formData.depot_id},depot_id.is.null`);
        }
        const { data } = await query;
        setAppUsers(data || []);
        // Auto-select current user as responsible person (use exact label from loaded data)
        if (currentUser && responsiblePersonMode === 'select') {
          const me = (data || []).find(u => u.email === currentUser.email);
          if (me) {
            const label = `${[me.first_name, me.surname].filter(Boolean).join(' ')} <${me.email}>`;
            setFormData(prev => ({ ...prev, requesting_person: label }));
          }
        }
      } catch (err) {
        console.error('Error loading app users:', err);
      } finally {
        setAppUsersLoading(false);
      }
    }
    if (isOpen) loadUsers();
  }, [isOpen, formData.depot_id]);

  // Load available briefs when depot changes
  useEffect(() => {
    async function loadBriefs() {
      setBriefsLoading(true);
      try {
        let query = supabase
          .from('induction_briefs')
          .select('id, title, version, depot_id, depots(name)')
          .eq('is_active', true)
          .order('title');

        if (formData.depot_id) {
          query = query.or(`depot_id.eq.${formData.depot_id},depot_id.is.null`);
        }

        const { data, error: briefsError } = await query;
        if (briefsError) {
          console.error('Error loading briefs:', briefsError);
          setAvailableBriefs([]);
        } else {
          setAvailableBriefs(data || []);
        }
      } catch (err) {
        console.error('Error loading briefs:', err);
        setAvailableBriefs([]);
      } finally {
        setBriefsLoading(false);
      }
    }

    if (isOpen) {
      loadBriefs();
    }
  }, [isOpen, formData.depot_id]);

  // Check induction history when email + selected briefs change
  useEffect(() => {
    const email = formData.person_email?.trim();
    const briefIds = formData.brief_ids;

    if (!email || briefIds.length === 0 || !email.includes('@')) {
      setInductionHistory({});
      return;
    }

    // Debounce: wait 500ms after last change
    const timer = setTimeout(async () => {
      setHistoryLoading(true);
      try {
        const { data, error: rpcErr } = await supabase.rpc('staff_check_induction_history', {
          p_person_email: email,
          p_brief_ids: briefIds,
        });

        if (!rpcErr && Array.isArray(data)) {
          const map = {};
          data.forEach(row => {
            map[row.brief_id] = row;
          });
          setInductionHistory(map);
        }
      } catch (err) {
        console.error('Error checking induction history:', err);
      } finally {
        setHistoryLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.person_email, formData.brief_ids]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let projectId = null;

      // ── Create project only for team/project mode ────────────────────
      const needsProject = inviteMode === 'team';
      if (needsProject) {
        if (!formData.project_name || !formData.estimated_start_date || !formData.estimated_end_date) {
          setError('Please fill in the project name and dates.');
          setIsSubmitting(false);
          return;
        }
      }
      if (needsProject) {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-project`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              project_name: formData.project_name,
              project_summary: formData.project_summary || '',
              site_id: formData.depot_id,
              estimated_start_date: formData.estimated_start_date,
              estimated_end_date: formData.estimated_end_date,
              project_lead_name: formData.person_name,
              project_lead_email: formData.person_email,
              project_lead_phone: formData.person_phone || '',
              project_lead_company: formData.person_company || '',
              created_by_admin_id: currentUser?.id,
              created_by_admin_name: currentUser?.name || `${currentUser?.first_name ?? ''} ${currentUser?.surname ?? ''}`.trim(),
              created_by_admin_email: currentUser?.email,
            }),
          }
        );
        const projectResult = await response.json();
        if (!response.ok) throw new Error(projectResult.error || 'Failed to create project');
        projectId = projectResult.id || projectResult.project_id || null;
      }

      // ── Project mode: admin-create-project already created the submission + sent email ──
      if (needsProject && projectId) {
        setSuccessMessage('Project created & invite sent!');
        setTimeout(() => {
          resetForm();
          onSuccess?.();
          onClose();
        }, 2000);
        return;
      }

      // ── Individual invite: create submission via RPC + send email ───────────
      const result = await createInvite({
        ...formData,
        nature_of_work: formData.reason_for_visit,
        forceFullInduction,
      });

      if (result.success) {
        setSuccessMessage(
          `Invite created successfully! Reference: ${result.reference}${
            result.email_sent ? ' Email sent.' : ''
          }`
        );
        setTimeout(() => {
          resetForm();
          onSuccess?.();
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to create invite');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setInviteMode('individual');
    setResponsiblePersonMode('select');
    setAppUsers([]);
    setFormData({
      person_name: '',
      person_email: '',
      person_company: '',
      person_phone: '',
      person_type: 'contractor',
      depot_id: defaultDepotId,
      visit_date: new Date().toISOString().split('T')[0],
      nature_of_work: '',
      work_area: '',
      reason_for_visit: '',
      requesting_person: '',
      expiry_hours: 72,
      send_email: true,
      brief_ids: [],
      project_name: '',
      project_summary: '',
      estimated_start_date: '',
      estimated_end_date: '',
    });
    setError(null);
    setSuccessMessage(null);
    setInductionHistory({});
    setForceFullInduction({});
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Create Invite
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Mode Selector ─────────────────────────────────────────────── */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setInviteMode('individual')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                inviteMode === 'individual'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              <User className="h-4 w-4" />
              Individual Invite
            </button>
            <button
              type="button"
              onClick={() => setInviteMode('team')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-l border-border ${
                inviteMode === 'team'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              <FolderKanban className="h-4 w-4" />
              Team / Project
            </button>
          </div>
          {inviteMode === 'team' && (
            <p className="-mt-3 text-xs text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-2">
              A new project will be created and the person below becomes the <strong>project lead</strong>. You can add team members from the Projects page.
            </p>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">Success</p>
                <p className="text-sm text-green-700 mt-1">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Person / Project Lead Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
              {inviteMode === 'team' ? (
                <><Users className="h-4 w-4" /> Project Lead Details</>
              ) : (
                <><User className="h-4 w-4" /> Person Details</>
              )}
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="person_name">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="person_name"
                  value={formData.person_name}
                  onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                  placeholder="John Smith"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="person_company">Company (Optional)</Label>
                <Input
                  id="person_company"
                  value={formData.person_company}
                  onChange={(e) => setFormData({ ...formData, person_company: e.target.value })}
                  placeholder="ACME Construction"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="person_email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="person_email"
                  type="email"
                  value={formData.person_email}
                  onChange={(e) => setFormData({ ...formData, person_email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="person_phone">Phone (Optional)</Label>
                <Input
                  id="person_phone"
                  type="tel"
                  value={formData.person_phone}
                  onChange={(e) => setFormData({ ...formData, person_phone: e.target.value })}
                  placeholder="07700 900000"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Person Type toggle — only in individual mode */}
            {inviteMode === 'individual' && (
              <div className="space-y-1.5">
                <Label>Person Type <span className="text-red-500">*</span></Label>
                <div className="flex rounded-lg border border-border overflow-hidden">
                  {[
                    { value: 'visitor',    label: 'Visitor',    Icon: Eye },
                    { value: 'employee',   label: 'Employee',   Icon: User },
                    { value: 'contractor', label: 'Contractor', Icon: HardHat },
                  ].map(({ value, label, Icon }, i) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, person_type: value })}
                      disabled={isSubmitting}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                        i > 0 ? 'border-l border-border' : ''
                      } ${
                        formData.person_type === value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Individual Invite — Visit Details ────────────────────────── */}
          {inviteMode === 'individual' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
                <Briefcase className="h-4 w-4" /> Visit Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {depots.length > 0 && (
                  <div className="col-span-2">
                    <Label htmlFor="depot_id_visit">
                      Depot / Site <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`visit-depot-${formData.depot_id || 'ALL'}-${depots.length}`}
                      value={formData.depot_id || 'ALL'}
                      onValueChange={(value) => setFormData({ ...formData, depot_id: value === 'ALL' ? null : value })}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="depot_id_visit">
                        <span className={formData.depot_id ? 'text-foreground text-sm' : 'text-muted-foreground text-sm'}>
                          {formData.depot_id
                            ? (depots.find(d => d.id === formData.depot_id)?.name || 'Select depot...')
                            : 'Select depot...'}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Sites</SelectItem>
                        {depots.map((depot) => (
                          <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label htmlFor="reason_for_visit">
                    Reason for Visit <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="reason_for_visit"
                    value={formData.reason_for_visit}
                    onChange={(e) => setFormData({ ...formData, reason_for_visit: e.target.value })}
                    placeholder="e.g. Site inspection, Delivery, Training"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Project Details — Team / Project mode only ──────────────────── */}
          {inviteMode === 'team' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
              <FolderKanban className="h-4 w-4" /> Project Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project_name">
                  Project Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="project_name"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  placeholder="e.g., Station Roof Refurbishment"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div>
                <Label htmlFor="project_summary">Project Summary (Optional)</Label>
                <Input
                  id="project_summary"
                  value={formData.project_summary}
                  onChange={(e) => setFormData({ ...formData, project_summary: e.target.value })}
                  placeholder="Brief description of the project scope"
                  disabled={isSubmitting}
                />
              </div>
              {depots.length > 0 && (
                <div className="col-span-2">
                  <Label htmlFor="depot_id">
                    Depot / Site <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    key={`depot-${formData.depot_id || 'ALL'}-${depots.length}`}
                    value={formData.depot_id || 'ALL'}
                    onValueChange={(value) => setFormData({ ...formData, depot_id: value === 'ALL' ? null : value })}
                    disabled={isSubmitting}
                    required
                  >
                    <SelectTrigger id="depot_id">
                      <span className={formData.depot_id ? 'text-foreground text-sm' : 'text-muted-foreground text-sm'}>
                        {formData.depot_id
                          ? (depots.find(d => d.id === formData.depot_id)?.name || 'Select depot...')
                          : 'Select depot...'}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Sites</SelectItem>
                      {depots.map((depot) => (
                        <SelectItem key={depot.id} value={depot.id}>
                          {depot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="estimated_start_date">
                  Start Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="estimated_start_date"
                  type="date"
                  value={formData.estimated_start_date}
                  onChange={(e) => setFormData({ ...formData, estimated_start_date: e.target.value })}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div>
                <Label htmlFor="estimated_end_date">
                  End Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="estimated_end_date"
                  type="date"
                  value={formData.estimated_end_date}
                  onChange={(e) => setFormData({ ...formData, estimated_end_date: e.target.value })}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            {/* Responsible Person */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  Responsible Person
                </Label>
                <button
                  type="button"
                  onClick={() => {
                    setResponsiblePersonMode(m => m === 'select' ? 'manual' : 'select');
                    setFormData(prev => ({ ...prev, requesting_person: '' }));
                  }}
                  className="text-xs text-primary underline underline-offset-2"
                  disabled={isSubmitting}
                >
                  {responsiblePersonMode === 'select' ? 'Enter manually instead' : 'Pick from users instead'}
                </button>
              </div>
              {responsiblePersonMode === 'select' ? (
                <Select
                  value={formData.requesting_person || ''}
                  onValueChange={(value) => setFormData({ ...formData, requesting_person: value })}
                  disabled={isSubmitting || appUsersLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={appUsersLoading ? 'Loading users...' : 'Select responsible person...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {appUsers.map((u) => {
                      const name = [u.first_name, u.surname].filter(Boolean).join(' ');
                      const label = `${name} <${u.email}>`;
                      // Plain text child is required so Radix SelectValue can display it in the trigger
                      return (
                        <SelectItem key={u.id} value={label}>
                          {name ? `${name} (${u.email})` : u.email}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.requesting_person}
                  onChange={(e) => setFormData({ ...formData, requesting_person: e.target.value })}
                  placeholder="e.g. Jane Smith or jane@example.com"
                  disabled={isSubmitting}
                />
              )}
            </div>
          </div>
          )}

          {/* Induction Brief Selection */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Induction Briefs <span className="text-red-500">*</span>
            </h3>
            <p className="text-xs text-muted-foreground">
              Select the induction brief(s) the visitor must complete. They will see these when they log in to the portal.
            </p>

            {briefsLoading && (
              <p className="text-sm text-muted-foreground">Loading available briefs...</p>
            )}

            {!briefsLoading && availableBriefs.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  No active induction briefs found{formData.depot_id ? ' for the selected depot' : ''}. Create a brief in the Induction Briefs tab first.
                </p>
              </div>
            )}

            {!briefsLoading && availableBriefs.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-border rounded-lg p-3">
                {availableBriefs.map((brief) => {
                  const history = inductionHistory[brief.id];
                  const isSelected = formData.brief_ids.includes(brief.id);
                  const isCompleted = history?.previously_completed;
                  return (
                    <div key={brief.id}>
                      <label
                        className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary/10 border border-primary/30'
                            : 'hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, brief_ids: [...formData.brief_ids, brief.id] });
                            } else {
                              setFormData({ ...formData, brief_ids: formData.brief_ids.filter((id) => id !== brief.id) });
                              // Clean up force flag when deselected
                              setForceFullInduction(prev => { const n = { ...prev }; delete n[brief.id]; return n; });
                            }
                          }}
                          className="mt-1 h-4 w-4 rounded border-border"
                          disabled={isSubmitting}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{brief.title}</p>
                          <p className="text-xs text-muted-foreground">
                            v{brief.version}{brief.depots?.name ? ` • ${brief.depots.name}` : ' • All Sites'}
                          </p>
                        </div>
                      </label>

                      {/* Previously completed banner */}
                      {isSelected && isCompleted && (
                        <div className="ml-7 mt-1 mb-1 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-md p-2.5">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-green-800 dark:text-green-300">
                                Previously completed{history.completed_at ? ` on ${new Date(history.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                                {history.project_name ? ` (${history.project_name})` : ''}
                              </p>
                              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                                They will only need to confirm they've reviewed it — no forms to fill in.
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Switch
                                  id={`force-${brief.id}`}
                                  checked={forceFullInduction[brief.id] || false}
                                  onCheckedChange={(checked) => {
                                    setForceFullInduction(prev => ({ ...prev, [brief.id]: checked }));
                                  }}
                                  disabled={isSubmitting}
                                  className="scale-90"
                                />
                                <Label htmlFor={`force-${brief.id}`} className="text-xs font-medium text-green-800 dark:text-green-300 cursor-pointer flex items-center gap-1">
                                  <RotateCcw className="h-3 w-3" />
                                  Require full re-induction
                                </Label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {historyLoading && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <History className="h-3 w-3 animate-spin" /> Checking induction history...
              </p>
            )}
          </div>

          {/* Invite Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2">Invite Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expiry_hours">Invite Expiry</Label>
                <Select
                  key={`expiry-${isOpen}`}
                  value={String(formData.expiry_hours)}
                  onValueChange={(value) => setFormData({ ...formData, expiry_hours: parseInt(value) })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="expiry_hours">
                    <span>
                      {({'48':'2 Days','72':'3 Days','168':'7 Days','336':'14 Days','720':'30 Days','4320':'6 Months','8760':'12 Months'})[String(formData.expiry_hours)] || 'Select expiry…'}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="48">2 Days</SelectItem>
                    <SelectItem value="72">3 Days</SelectItem>
                    <SelectItem value="168">7 Days</SelectItem>
                    <SelectItem value="336">14 Days</SelectItem>
                    <SelectItem value="720">30 Days</SelectItem>
                    <SelectItem value="4320">6 Months</SelectItem>
                    <SelectItem value="8760">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3 pt-8">
                <Switch
                  id="send_email"
                  checked={formData.send_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
                  disabled={isSubmitting}
                />
                <Label htmlFor="send_email" className="cursor-pointer">
                  Send email immediately
                </Label>
              </div>
            </div>

            {!formData.send_email && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Invite will be created but email will NOT be sent. You can manually send it later.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={(() => {
                if (isSubmitting || !formData.person_name || !formData.person_email || formData.brief_ids.length === 0) return true;
                if (inviteMode === 'individual') {
                  return !formData.reason_for_visit;
                }
                return !formData.project_name || !formData.estimated_start_date || !formData.estimated_end_date;
              })()}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  {inviteMode === 'team' ? 'Creating Project...' : 'Sending...'}
                </>
              ) : (
                <>
                  {inviteMode === 'team' ? (
                    <><FolderKanban className="h-4 w-4 mr-2" /> Create Project &amp; Invite Lead</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send Invite</>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInviteModal;
